/**
 * TrlMapBuilder — admin galaxy-map authoring surface.
 *
 * Owns the editable map state (nodes/edges in normalized coords), runs the
 * shared topology + validation live, saves through TRL_SAVE_MAP on explicit
 * user action, and renders the canvas plus a top toolbar and two modals:
 *   • GameInfoModal — full round breakdown + validation report
 *   • PlanetEditModal — all-in-one planet editor (images, checkpoint, delete)
 *
 * The concrete graph library is isolated in TrlReactFlowCanvas behind the
 * TrlMapCanvasProps contract; this component contains no React-Flow specifics.
 *
 * Answer coordinates never enter here: nodes reference library images by id and
 * carry only the public (coord-stripped) metadata for rendering.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  deriveNodeLevels,
  getStartNodes,
  getFinalNode,
  validateMap,
  trlImageAreaKey,
  isTrlSabotageTypeEnabled,
  DEFAULT_TRL_NODE_SETTINGS,
  TRL_CANVAS_WIDTH,
  TRL_CANVAS_HEIGHT,
  type TrlMapDef,
  type TrlMapNodeDef,
  type TrlMapEdgeDef,
  type TrlMapBackground,
  type TrlNodeImageRef,
  type TrlEdgeStyle,
  type TopoNode,
  type TopoEdge,
} from '@swtorbit/shared-socket-events';
import { useTrlContext } from '../../../contexts/TrlContext';
import { useGameContext } from '../../../contexts/GameContext';
import { useTrlImageLibrary } from '../../content-library';
import { TrlReactFlowCanvas } from './TrlReactFlowCanvas';
import { GameInfoModal } from './GameInfoModal';
import { PlanetEditModal, type TrlNodeConfigPatch } from './PlanetEditModal';
import { PathEditModal } from './PathEditModal';
import { TrlGameSettingsModal } from './TrlGameSettingsModal';
import { MapSettingsModal, type MapSettingsResult } from './MapSettingsModal';
import { TrlExportModal } from './bundle/TrlExportModal';
import { TrlImportModal } from './bundle/TrlImportModal';
import { buildSaveMapRequest, type TrlImportPlan } from './bundle/trlBundle';
import { emitConfigWrite } from './configWrite';

interface TrlMapBuilderProps {
  roomCode: string;
  /**
   * True once the game reaches the lobby: the map is locked for the rest of the
   * game. The builder renders read-only (pan/zoom only) — all edits, saves and
   * the planet editor are disabled. The server also blocks saves past setup.
   */
  readOnly?: boolean;
}

export function TrlMapBuilder({ roomCode, readOnly = false }: TrlMapBuilderProps) {
  const {
    saveMap,
    loadMap,
    updateTreasureConfig,
    updateStalemateConfig,
    updateRadarConfig,
    updateTriangulationConfig,
    updateDisruptionConfig,
    updateSabotageConfig,
    updateTeamNameConfig,
  } = useTrlContext();
  const { selectedGame } = useGameContext();
  const { images, imagesLoading } = useTrlImageLibrary();

  const [nodes, setNodes] = useState<TrlMapNodeDef[]>([]);
  const [edges, setEdges] = useState<TrlMapEdgeDef[]>([]);
  const [background, setBackground] = useState<TrlMapBackground | undefined>(undefined);
  const [defaultEdgeStyle, setDefaultEdgeStyle] = useState<TrlEdgeStyle | undefined>(undefined);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [planetEditNodeId, setPlanetEditNodeId] = useState<string | null>(null);
  const [pathEditEdgeId, setPathEditEdgeId] = useState<string | null>(null);
  const [gameInfoOpen, setGameInfoOpen] = useState(false);
  const [gameSettingsOpen, setGameSettingsOpen] = useState(false);
  const [mapSettingsOpen, setMapSettingsOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  const backgroundRef = useRef(background);
  const defaultEdgeStyleRef = useRef(defaultEdgeStyle);
  nodesRef.current = nodes;
  edgesRef.current = edges;
  backgroundRef.current = background;
  defaultEdgeStyleRef.current = defaultEdgeStyle;
  const tmpCounter = useRef(0);
  const versionRef = useRef(0);

  const adoptMap = useCallback((map: TrlMapDef) => {
    setNodes(map.nodes);
    setEdges(map.edges);
    setBackground(map.background);
    setDefaultEdgeStyle(map.defaultEdgeStyle);
    setDirty(false);
  }, []);

  useEffect(() => {
    setLoading(true);
    loadMap({ roomCode }, (resp) => {
      if (resp.success && resp.map) {
        adoptMap(resp.map);
        setLoadError(null);
      } else {
        setLoadError(resp.error ?? 'Failed to load map');
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCode]);

  const doSave = useCallback(() => {
    const reqVersion = versionRef.current;
    const req = buildSaveMapRequest(roomCode, TRL_CANVAS_WIDTH, TRL_CANVAS_HEIGHT, {
      nodes: nodesRef.current,
      edges: edgesRef.current,
      background: backgroundRef.current,
      defaultEdgeStyle: defaultEdgeStyleRef.current,
    });
    setSaving(true);
    saveMap(req, (resp) => {
      setSaving(false);
      if (resp.success && resp.map) {
        setLastSavedAt(Date.now());
        if (versionRef.current === reqVersion) {
          adoptMap(resp.map);
        }
      }
    });
  }, [roomCode, saveMap, adoptMap]);

  const saveNow = useCallback(() => {
    doSave();
  }, [doSave]);

  /** Record a local edit: bump version and mark dirty. Save is manual only. */
  const touch = useCallback(() => {
    versionRef.current += 1;
    setDirty(true);
  }, []);

  /**
   * Replace every node's treasure assignment (from a re-roll or a hand edit in
   * the Game Settings modal). Nodes absent from the map are cleared, so this is
   * a wholesale swap rather than a merge.
   */
  const applyTreasureAssignments = useCallback(
    (assignments: Record<string, string[]>) => {
      setNodes((prev) =>
        prev.map((n) => {
          const ids = assignments[n.id];
          return { ...n, treasureItemIds: ids && ids.length > 0 ? ids : undefined };
        }),
      );
      touch();
    },
    [touch],
  );

  // ── Import / export design bundles ─────────────────────────────────────────

  /**
   * Commit a resolved import: every ticked section, persisted in one go.
   *
   * Config sections go first and are awaited together. That ordering is
   * load-bearing rather than tidy: the server prunes each node's
   * `treasureItemIds` against the game's CURRENT pool on save, so a map saved
   * before its treasure list landed would arrive with every placement stripped.
   *
   * Resolves with an error message, or null once everything is persisted.
   */
  const applyImport = useCallback(
    async (plan: TrlImportPlan): Promise<string | null> => {
      const writes: Array<Promise<string | null>> = [];
      const { treasure } = plan;
      const { triangulation, stalemate, radar, disruption, sabotage, teamNames } = plan.configs;

      if (treasure) {
        writes.push(
          emitConfigWrite((cb) => updateTreasureConfig({ roomCode, config: treasure }, cb), 'the treasure list'),
        );
      }
      if (triangulation) {
        writes.push(
          emitConfigWrite((cb) => updateTriangulationConfig({ roomCode, config: triangulation }, cb), 'triangulation'),
        );
      }
      if (stalemate) {
        writes.push(
          emitConfigWrite((cb) => updateStalemateConfig({ roomCode, config: stalemate }, cb), 'stalemate clues'),
        );
      }
      if (radar) {
        writes.push(emitConfigWrite((cb) => updateRadarConfig({ roomCode, config: radar }, cb), 'radar ranges'));
      }
      if (disruption) {
        writes.push(
          emitConfigWrite((cb) => updateDisruptionConfig({ roomCode, config: disruption }, cb), 'disruption'),
        );
      }
      if (sabotage) {
        writes.push(emitConfigWrite((cb) => updateSabotageConfig({ roomCode, config: sabotage }, cb), 'sabotage'));
      }
      if (teamNames) {
        // This one event takes the name lists flat rather than wrapped in `config`.
        const { disabledCoreNames, customNames } = teamNames;
        writes.push(
          emitConfigWrite(
            (cb) => updateTeamNameConfig({ roomCode, disabledCoreNames, customNames }, cb),
            'team names',
          ),
        );
      }

      const failures = (await Promise.all(writes)).filter((r): r is string => r !== null);
      if (failures.length > 0) return failures.join(' · ');

      if (!plan.map) return null;

      // Save straight from the hydrated map rather than from state: `nodesRef`
      // is only reassigned during render, so setting state here and calling the
      // usual save would persist the map we are replacing.
      const { nodes: newNodes, edges: newEdges, background: newBackground, defaultEdgeStyle: newEdgeStyle } = plan.map;
      const req = buildSaveMapRequest(roomCode, TRL_CANVAS_WIDTH, TRL_CANVAS_HEIGHT, {
        nodes: newNodes,
        edges: newEdges,
        background: newBackground,
        defaultEdgeStyle: newEdgeStyle,
      });

      setSaving(true);
      const reqVersion = (versionRef.current += 1);
      return new Promise<string | null>((resolve) => {
        saveMap(req, (resp) => {
          setSaving(false);
          if (!resp.success || !resp.map) {
            resolve(resp.error ?? 'Failed to save the imported galaxy map.');
            return;
          }
          setSelectedNodeId(null);
          setLastSavedAt(Date.now());
          // Adopt the server's reconciled map (real ids, derived topology) so the
          // builder lands clean rather than dirty with throwaway `tmp:` ids.
          if (versionRef.current === reqVersion) adoptMap(resp.map);
          resolve(null);
        });
      });
    },
    [
      roomCode,
      saveMap,
      adoptMap,
      updateTreasureConfig,
      updateTriangulationConfig,
      updateStalemateConfig,
      updateRadarConfig,
      updateDisruptionConfig,
      updateSabotageConfig,
      updateTeamNameConfig,
    ],
  );

  // ── Graph mutations ──────────────────────────────────────────────────────

  const addNode = useCallback(() => {
    setNodes((prev) => {
      const n: TrlMapNodeDef = {
        id: `tmp:n:${(tmpCounter.current += 1)}`,
        label: `Planet ${prev.length + 1}`,
        posX: 0.15 + ((prev.length * 0.09) % 0.7),
        posY: 0.2 + ((prev.length * 0.13) % 0.6),
        isCheckpoint: DEFAULT_TRL_NODE_SETTINGS.isCheckpoint,
        isStart: false,
        isFinal: false,
        imagesToClear: DEFAULT_TRL_NODE_SETTINGS.imagesToClear,
        images: [],
        roundType: DEFAULT_TRL_NODE_SETTINGS.roundType,
        triangulationTarget: undefined,
        basePoints: DEFAULT_TRL_NODE_SETTINGS.basePoints,
        pointsDecrement: DEFAULT_TRL_NODE_SETTINGS.pointsDecrement,
        stalemateOverride: null,
        radarOverride: null,
        icon: null,
        iconColor: null,
      };
      return [...prev, n];
    });
    touch();
  }, [touch]);

  const moveNode = useCallback(
    (id: string, posX: number, posY: number) => {
      setNodes((prev) => prev.map((n) => (n.id === id ? { ...n, posX, posY } : n)));
      touch();
    },
    [touch]
  );

  const connectNodes = useCallback(
    (from: string, to: string) => {
      if (from === to) return;
      setEdges((prev) => {
        if (prev.some((e) => e.fromNodeId === from && e.toNodeId === to)) return prev;
        return [
          ...prev,
          { id: `tmp:e:${(tmpCounter.current += 1)}`, fromNodeId: from, toNodeId: to, controlPoints: [] },
        ];
      });
      touch();
    },
    [touch]
  );

  const deleteNode = useCallback(
    (id: string) => {
      setNodes((prev) => prev.filter((n) => n.id !== id));
      setEdges((prev) => prev.filter((e) => e.fromNodeId !== id && e.toNodeId !== id));
      setSelectedNodeId((cur) => (cur === id ? null : cur));
      touch();
    },
    [touch]
  );

  const deleteEdge = useCallback(
    (id: string) => {
      setEdges((prev) => prev.filter((e) => e.id !== id));
      touch();
    },
    [touch]
  );

  const setEdgeControlPoints = useCallback(
    (id: string, controlPoints: Array<{ x: number; y: number }>) => {
      setEdges((prev) => prev.map((e) => (e.id === id ? { ...e, controlPoints } : e)));
      touch();
    },
    [touch]
  );

  /**
   * Set (or, with `undefined`, clear) one path's own style. Clearing drops the
   * override so the path inherits the map-wide default again.
   */
  const setEdgeStyle = useCallback(
    (id: string, style: TrlEdgeStyle | undefined) => {
      setEdges((prev) => prev.map((e) => (e.id === id ? { ...e, style } : e)));
      touch();
    },
    [touch]
  );

  /** Apply both settings chosen in the Map Settings modal in one edit. */
  const applyMapSettings = useCallback(
    (next: MapSettingsResult) => {
      setBackground(next.background);
      setDefaultEdgeStyle(next.defaultEdgeStyle);
      touch();
    },
    [touch]
  );


  const setNodeConfig = useCallback(
    (id: string, patch: TrlNodeConfigPatch) => {
      const {
        imageIds,
        isCheckpoint,
        roundType,
        triangulationTarget,
        stalemateOverride,
        radarOverride,
        icon,
        iconColor,
        treasureItemIds,
        decoyImageId,
      } = patch;
      const lib = new Map(images.map((i) => [i.id, i]));
      const decoyLib = decoyImageId ? lib.get(decoyImageId) : undefined;
      setNodes((prev) => {
        // A round is either a checkpoint round or it isn't: flipping one planet's
        // checkpoint flips every planet in the same round with it. Validation
        // still guards rounds that drift apart later as connections change.
        const levels = deriveNodeLevels(
          prev.map((n) => ({ id: n.id })),
          edgesRef.current.map((e) => ({ fromNodeId: e.fromNodeId, toNodeId: e.toNodeId })),
        );
        const editedNode = prev.find((n) => n.id === id);
        const roundLevel = levels.get(id);
        const syncRound = editedNode !== undefined && editedNode.isCheckpoint !== isCheckpoint && roundLevel !== undefined;

        return prev.map((n) => {
          if (n.id !== id) {
            return syncRound && levels.get(n.id) === roundLevel ? { ...n, isCheckpoint } : n;
          }
          const effectiveToClear = roundType === 'triangulation' ? 3 : 1;
          const refs: TrlNodeImageRef[] = imageIds.map((imageId, idx) => {
            const existing = n.images.find((r) => r.imageId === imageId);
            const li = lib.get(imageId);
            return {
              id: existing?.id ?? `tmp:i:${(tmpCounter.current += 1)}`,
              imageId,
              sortOrder: idx,
              imageUrl: li?.url,
              imageName: li?.name,
              imageArea: li?.area,
              logAreaId: li?.logAreaId,
              faction: li?.faction,
            };
          });
          return {
            ...n,
            images: refs,
            imagesToClear: effectiveToClear,
            isCheckpoint,
            roundType,
            triangulationTarget,
            stalemateOverride,
            radarOverride,
            icon,
            iconColor,
            treasureItemIds: treasureItemIds.length > 0 ? treasureItemIds : undefined,
            decoyImage: decoyImageId
              ? {
                  id: n.decoyImage?.id ?? `decoy:${n.id}`,
                  imageId: decoyImageId,
                  sortOrder: -1,
                  imageUrl: decoyLib?.url,
                  imageName: decoyLib?.name,
                  imageArea: decoyLib?.area,
                  logAreaId: decoyLib?.logAreaId,
                  faction: decoyLib?.faction,
                }
              : undefined,
          };
        });
      });
      touch();
    },
    [images, touch]
  );

  // ── Live topology + validation ───────────────────────────────────────────

  // Con Clue sabotage obliges a fake image on every eligible planet.
  const requireDecoyImages = isTrlSabotageTypeEnabled(selectedGame?.trlSabotageConfig, 'con_clue');
  // Scanner Spy can only sit on a Triangulation planet, so the map needs one.
  const requireTriangulationNode = isTrlSabotageTypeEnabled(selectedGame?.trlSabotageConfig, 'scanner_spy');

  const { decoratedNodes, validation } = useMemo(() => {
    const topoNodes: TopoNode[] = nodes.map((n) => ({ id: n.id }));
    const topoEdges: TopoEdge[] = edges.map((e) => ({ fromNodeId: e.fromNodeId, toNodeId: e.toNodeId }));
    const levels = deriveNodeLevels(topoNodes, topoEdges);
    const startSet = new Set(getStartNodes(topoNodes, topoEdges));
    const finalId = getFinalNode(topoNodes, topoEdges);
    const decorated: TrlMapNodeDef[] = nodes.map((n) => ({
      ...n,
      derivedLevel: levels.get(n.id),
      isStart: startSet.has(n.id),
      isFinal: n.id === finalId,
    }));
    const result = validateMap(
      nodes.map((n) => ({
        id: n.id,
        imageCount: n.images.length,
        imagesToClear: n.imagesToClear,
        isCheckpoint: n.isCheckpoint,
        roundType: n.roundType,
        imageAreaKeys: n.images.map(trlImageAreaKey).filter((key): key is string => !!key),
        imageFactions: n.images.map((i) => i.faction).filter((f): f is string => !!f),
        hasTriangulationTarget: n.triangulationTarget !== undefined,
        imageIds: n.images.map((i) => i.imageId),
        decoyImageId: n.decoyImage?.imageId ?? null,
        decoyAreaKey: trlImageAreaKey(n.decoyImage),
        decoyFaction: n.decoyImage?.faction,
      })),
      topoEdges,
      { requireDecoyImages, requireTriangulationNode },
    );
    return { decoratedNodes: decorated, validation: result };
  }, [nodes, edges, requireDecoyImages, requireTriangulationNode]);

  // Round counts for the toolbar badge
  const rCounts = useMemo(() => {
    const m = new Map<number, number>();
    for (const n of decoratedNodes) {
      if (n.derivedLevel) m.set(n.derivedLevel, (m.get(n.derivedLevel) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]);
  }, [decoratedNodes]);

  const planetEditNode = planetEditNodeId ? decoratedNodes.find((n) => n.id === planetEditNodeId) : undefined;
  const pathEditEdge = pathEditEdgeId ? edges.find((e) => e.id === pathEditEdgeId) : undefined;
  const lastSavedLabel = lastSavedAt ? new Date(lastSavedAt).toLocaleTimeString() : null;

  if (loading) {
    return <p className="text-slate">Loading galaxy map…</p>;
  }
  if (loadError) {
    return <div className="p-xs rounded-xs bg-red text-white">Failed to load map: {loadError}</div>;
  }

  return (
    <div className="d-flex flex-column gap-sm">
      {readOnly && (
        <div className="bg-space bg-o-50 border-md border-amber rounded-base p-xs d-flex align-items-center gap-xs flex-wrap">
          <strong className="text-amber">🔒 Map locked</strong>
          <span className="text-slate">The galaxy map cannot be edited once the game is in the lobby.</span>
        </div>
      )}

      {/* Top toolbar: ADD | IMPORT | GAME INFO | EXPORT | SAVE (edit actions hidden when locked) */}
      <div className="d-flex align-items-center gap-sm">
        {!readOnly && (
          <button type="button" className="btn btn--outline btn--slate" onClick={addNode}>
            ADD
          </button>
        )}

        {!readOnly && (
          <button
            type="button"
            className="btn btn--outline btn--slate"
            onClick={() => setImportOpen(true)}
            title="Bring a saved map, treasure list or rules into this game"
          >
            IMPORT
          </button>
        )}

        <button
          type="button"
          className="btn btn--outline btn--slate flex-1 d-flex align-items-center justify-content-center gap-xs flex-wrap"
          onClick={() => setGameInfoOpen(true)}
        >
          <strong>GAME INFO</strong>
          {rCounts.map(([level, count]) => (
            <span key={level} className="fs-xs">
              R{level}:{count}
            </span>
          ))}
          <span className="fs-xs">WARNINGS: {validation.errors.length}</span>
          <span
            className={`trl-map-builder__status-dot ${validation.ok ? 'bg-green-dark' : 'bg-red'}`}
            title={validation.ok ? 'Map is valid' : 'Map has errors'}
          />
        </button>

        <button
          type="button"
          className="btn btn--outline btn--slate"
          onClick={() => setExportOpen(true)}
          title="Download this game's map, treasure list and rules as a JSON file"
        >
          EXPORT
        </button>

        {!readOnly && (
          <button
            type="button"
            className="btn btn--outline btn--slate"
            onClick={() => setGameSettingsOpen(true)}
            title="Edit this game's settings (stalemate clues, radar ranges, sabotage, treasure list)"
          >
            Game Settings
          </button>
        )}

        {!readOnly && (
          <button
            type="button"
            className="btn btn--outline btn--slate"
            onClick={() => setMapSettingsOpen(true)}
            title="Choose the galaxy map's backdrop and the default style for its paths"
          >
            Map Settings
          </button>
        )}

        {!readOnly && (
          <button
            type="button"
            className="btn"
            onClick={saveNow}
            disabled={saving || !dirty}
            title={lastSavedLabel ? `Last saved ${lastSavedLabel}` : undefined}
          >
            {saving ? 'SAVING…' : dirty ? 'SAVE' : 'SAVED'}
          </button>
        )}

      </div>

      <TrlReactFlowCanvas
        nodes={decoratedNodes}
        edges={edges}
        background={background}
        canvasWidth={TRL_CANVAS_WIDTH}
        canvasHeight={TRL_CANVAS_HEIGHT}
        selectedNodeId={selectedNodeId}
        readOnly={readOnly}
        defaultEdgeStyle={defaultEdgeStyle}
        onSelectNode={setSelectedNodeId}
        onMoveNode={moveNode}
        onConnectNodes={connectNodes}
        onDeleteNode={deleteNode}
        onDeleteEdge={deleteEdge}
        onEdgeControlPointsChange={setEdgeControlPoints}
        onEdgeContextMenu={readOnly ? () => {} : setPathEditEdgeId}
        // Not gated on readOnly: the dots carry no markers or hover card any
        // more, so this modal is where a planet's details are read — on a locked
        // map it opens read-only instead of not opening at all.
        onOpenNode={setPlanetEditNodeId}
      />

      {gameInfoOpen && (
        <GameInfoModal
          nodes={decoratedNodes}
          validation={validation}
          onClose={() => setGameInfoOpen(false)}
        />
      )}

      {planetEditNode && (
        <PlanetEditModal
          node={planetEditNode}
          readOnly={readOnly}
          gameStalemateConfig={selectedGame?.trlStalemateConfig}
          gameRadarConfig={selectedGame?.trlRadarConfig}
          gameTreasureConfig={selectedGame?.trlTreasureConfig}
          conClueEnabled={requireDecoyImages}
          onConfirm={(patch) => {
            setNodeConfig(planetEditNode.id, patch);
            setPlanetEditNodeId(null);
          }}
          onDelete={() => {
            deleteNode(planetEditNode.id);
            setPlanetEditNodeId(null);
          }}
          onCancel={() => setPlanetEditNodeId(null)}
        />
      )}

      {pathEditEdge && (
        <PathEditModal
          style={pathEditEdge.style}
          mapDefault={defaultEdgeStyle}
          onConfirm={(style) => {
            setEdgeStyle(pathEditEdge.id, style);
            setPathEditEdgeId(null);
          }}
          onReset={() => {
            setEdgeStyle(pathEditEdge.id, undefined);
            setPathEditEdgeId(null);
          }}
          onDelete={() => {
            deleteEdge(pathEditEdge.id);
            setPathEditEdgeId(null);
          }}
          onCancel={() => setPathEditEdgeId(null)}
        />
      )}

      {gameSettingsOpen && (
        <TrlGameSettingsModal
          roomCode={roomCode}
          current={selectedGame?.trlStalemateConfig}
          currentRadar={selectedGame?.trlRadarConfig}
          currentTreasure={selectedGame?.trlTreasureConfig}
          currentTriangulation={selectedGame?.trlTriangulationConfig}
          currentDisruption={selectedGame?.trlDisruptionConfig}
          currentSabotage={selectedGame?.trlSabotageConfig}
          teams={selectedGame?.trlTeams}
          nodes={decoratedNodes}
          onTreasureAssignmentsChange={applyTreasureAssignments}
          onSaveMap={saveNow}
          onClose={() => setGameSettingsOpen(false)}
        />
      )}

      {exportOpen && (
        <TrlExportModal
          sources={{
            roomCode,
            canvasWidth: TRL_CANVAS_WIDTH,
            canvasHeight: TRL_CANVAS_HEIGHT,
            nodes,
            edges,
            background,
            defaultEdgeStyle,
            treasure: selectedGame?.trlTreasureConfig,
            stalemate: selectedGame?.trlStalemateConfig,
            radar: selectedGame?.trlRadarConfig,
            triangulation: selectedGame?.trlTriangulationConfig,
            disruption: selectedGame?.trlDisruptionConfig,
            sabotage: selectedGame?.trlSabotageConfig,
            teamNames: selectedGame?.trlTeamNameConfig,
          }}
          onClose={() => setExportOpen(false)}
        />
      )}

      {importOpen && (
        <TrlImportModal
          images={images}
          imagesLoading={imagesLoading}
          hasExistingMap={nodes.length > 0}
          onImport={applyImport}
          onClose={() => setImportOpen(false)}
        />
      )}

      {mapSettingsOpen && (
        <MapSettingsModal
          background={background}
          defaultEdgeStyle={defaultEdgeStyle}
          onConfirm={(next) => {
            applyMapSettings(next);
            setMapSettingsOpen(false);
          }}
          onCancel={() => setMapSettingsOpen(false)}
        />
      )}
    </div>
  );
}
